<script setup>
import OverviewStatusTag from './OverviewStatusTag.vue'

// RED-317 §8 — the county-independent overviews: Majandusülevaade (the
// report's §4.1, half-yearly) and the national Metsamaa / Põllumaa market
// overviews (quarterly). Same click-to-open, dot, period and "tuleb <date>"
// as a matrix cell.
defineProps({
  purposes: { type: Array, required: true },
  overviewsByCell: { type: Object, required: true },
  selectedKey: { type: String, default: null },
})

const emit = defineEmits(['select'])

const keyOf = purpose => `|${purpose.id}`

const formatDate = value => (value ? new Date(`${value}T00:00:00`).toLocaleDateString('et-EE') : '')
</script>

<template>
  <div>
    <h3 class="mb-2 text-sm font-medium text-gray-600">{{ $t('messages.admin.market_overviews.national.title') }}</h3>
    <div class="grid grid-cols-1 gap-3 md:grid-cols-3">
      <button
        v-for="purpose in purposes"
        :key="purpose.id"
        type="button"
        class="flex cursor-pointer flex-col gap-2 rounded-lg px-4 py-3.5 text-left transition-colors"
        :class="
          selectedKey === keyOf(purpose)
            ? 'border-2 border-primary-500 bg-primary-50'
            : 'border border-gray-200 bg-white hover:border-gray-300'
        "
        :aria-pressed="selectedKey === keyOf(purpose)"
        :data-testid="`mo-national-${purpose.id}`"
        @click="emit('select', { county: null, purpose: purpose.id })"
      >
        <span class="flex items-center justify-between gap-2">
          <span class="text-sm font-semibold text-gray-900">
            {{ purpose.label }}
            <i v-if="overviewsByCell[keyOf(purpose)]?.has_draft" class="pi pi-pencil ml-1 text-[0.65rem] text-primary-700" :title="$t('messages.admin.market_overviews.matrix.draft')" />
          </span>
          <OverviewStatusTag
            :status="overviewsByCell[keyOf(purpose)]?.status ?? 'missing'"
            :text="overviewsByCell[keyOf(purpose)]?.live?.period ?? null"
          />
        </span>
        <span class="text-xs text-gray-500">
          {{
            purpose.section === 'economy'
              ? $t('messages.admin.market_overviews.national.cadence_half_year')
              : $t('messages.admin.market_overviews.national.cadence_quarter')
          }}<template v-if="overviewsByCell[keyOf(purpose)]?.live">
            ·
            {{
              $t('messages.admin.market_overviews.national.valid_from', {
                date: formatDate(overviewsByCell[keyOf(purpose)].live.valid_from),
              })
            }}</template
          >
        </span>
        <span
          v-if="overviewsByCell[keyOf(purpose)]?.scheduled"
          class="text-xs text-sky-700"
          :data-testid="`mo-national-${purpose.id}-scheduled`"
        >
          {{
            $t('messages.admin.market_overviews.status.scheduled', {
              date: formatDate(overviewsByCell[keyOf(purpose)].scheduled.valid_from),
            })
          }}
        </span>
      </button>
    </div>
  </div>
</template>
